import { forwardRef, useImperativeHandle, useEffect, useRef } from 'react';

// Touch/pointer signature pad. White background, dark ink. Exposes clear()/isEmpty()/toDataURL().
const SignaturePad = forwardRef(function SignaturePad(props, ref) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  function prime(ctx, w, h) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
  }

  useEffect(() => {
    const c = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = Math.round(rect.width * dpr);
    c.height = Math.round(rect.height * dpr);
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    prime(ctx, rect.width, rect.height);
    ctxRef.current = ctx;
  }, []);

  function pos(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }
  function down(e) {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
    canvasRef.current.setPointerCapture?.(e.pointerId);
  }
  function move(e) {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = ctxRef.current;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    dirty.current = true;
  }
  function up() { drawing.current = false; }

  useImperativeHandle(ref, () => ({
    clear() {
      const c = canvasRef.current;
      const rect = c.getBoundingClientRect();
      prime(ctxRef.current, rect.width, rect.height);
      dirty.current = false;
    },
    isEmpty() { return !dirty.current; },
    toDataURL() { return canvasRef.current.toDataURL('image/png'); },
    // Crop to the inked area (with a little padding) so the signature isn't surrounded by
    // a large empty canvas — keeps it sized and positioned predictably when placed on a form.
    toTrimmedDataURL() {
      const c = canvasRef.current;
      if (!dirty.current) return c.toDataURL('image/png');
      const w = c.width, h = c.height;
      const data = ctxRef.current.getImageData(0, 0, w, h).data;
      let minX = w, minY = h, maxX = 0, maxY = 0, found = false;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4;
          if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) {
            found = true;
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
          }
        }
      }
      if (!found) return c.toDataURL('image/png');
      const pad = Math.round(8 * (window.devicePixelRatio || 1));
      minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
      maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);
      const tw = maxX - minX + 1, th = maxY - minY + 1;
      const out = document.createElement('canvas');
      out.width = tw; out.height = th;
      const octx = out.getContext('2d');
      octx.fillStyle = '#ffffff';
      octx.fillRect(0, 0, tw, th);
      octx.drawImage(c, minX, minY, tw, th, 0, 0, tw, th);
      return out.toDataURL('image/png');
    },
  }));

  return (
    <canvas
      ref={canvasRef}
      className="sig-pad"
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerLeave={up}
      style={{ touchAction: 'none' }}
    />
  );
});

export default SignaturePad;
